import { useState } from 'react';
import { VStack, Box, Text, HStack, Flex, Popover, Portal, For, Stack } from '@chakra-ui/react';
import { Avatar, ChatAvatar, EmptyContent, GroupIcon } from '@halo/ui-chat-react';
import { SessionIsCreator } from '@halo/chat-sdk-react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

function MessageStrangerCard({
  avatar,
  sessionName,
  userCardData,
  is_creator,
  duration,
}: {
  avatar: string | undefined;
  sessionName: string;
  userCardData?: any;
  is_creator?: SessionIsCreator;
  duration?: number;
}) {
  const { t } = useTranslation();
  const { userCard = null, userGroups = [], isSend = true } = userCardData || {};
  const [open, setOpen] = useState(false);

  if (is_creator === SessionIsCreator.CREATOR || isSend) return null;

  return (
    <VStack
      className="MessageStrangerCard fadeIn"
      py="6"
      px="4"
      gap="4"
      mx="auto"
      bg="white"
      borderRadius="2xl"
      textAlign="center"
      w="354px"
    >
      <ChatAvatar src={avatar} size="xl" duration={duration} />
      <Box>
        <Box fontWeight="semibold" fontSize="md" color="fg" lineHeight="normal">
          {sessionName}
        </Box>
        <Text color="fg.muted" fontWeight="medium" fontSize="xs">
          {t('chat.notAContact')}
        </Text>
      </Box>
      <Box fontSize="sm" fontWeight="normal" w="full">
        <HStack gap="2">
          <Box color="fg.muted" flex="1" textAlign="right">
            {t('chat.registration')}
          </Box>
          <Box color="fg" flex="1" textAlign="left" fontWeight="normal">
            {dayjs(userCard?.register_time).format('MMM YYYY')}
          </Box>
        </HStack>
        <HStack gap="2" mt="1.5">
          <Box color="fg.muted" flex="1" textAlign="right">
            {t('chat.mutualGroup')}
          </Box>
          <Popover.Root
            open={open}
            onOpenChange={(e) => setOpen(e.open)}
            positioning={{ placement: 'top' }}
          >
            <Popover.Trigger asChild cursor="pointer">
              <Flex color="fg" flex="1" textAlign="left" gap="1" align="center">
                <Box fontWeight="medium">
                  {userCard?.groups_num === 1
                    ? t('filter.groupsOne', { count: userCard?.groups_num })
                    : t('filter.groupsOther', { count: userCard?.groups_num })}
                </Box>
                <HStack alignItems="center" justifyContent="center" spaceX="-3">
                  {userGroups?.slice(0, 3).map((group: any) => (
                    <Avatar
                      src={group?.avatar}
                      key={group?.gid}
                      w="24px"
                      h="24px"
                      css={{
                        outlineWidth: '2px',
                        outlineColor: 'bg',
                        outlineStyle: 'solid',
                      }}
                      cursor="pointer"
                    />
                  ))}
                </HStack>
              </Flex>
            </Popover.Trigger>
            <Portal disabled>
              <Popover.Positioner>
                <Popover.Content rounded="lg" w="fit-content" maxW="300px">
                  <Popover.Arrow />
                  <Popover.Body p="1">
                    <Stack className="groups" maxH="300px" gap="0">
                      <Flex
                        align="center"
                        color="fg"
                        fontSize="xl"
                        fontWeight="semibold"
                        py="2.5"
                        pl="2.5"
                      >
                        {userCard?.groups_num}
                        <Text ml="0.5" color="fg.muted" fontSize="xs" fontWeight="medium">
                          {userCard?.groups_num === 1
                            ? t('chat.mutualGroup')
                            : t('chat.mutualGroups')}
                        </Text>
                      </Flex>
                      <Box className="group-list scrollHover" overflowY="auto" flex="1">
                        {userGroups.length > 0 ? (
                          <For each={userGroups}>
                            {(group: any) => (
                              <Flex
                                className="group-item"
                                key={group?.gid}
                                pt="2"
                                pb="2.5"
                                gap="2.5"
                                px="2"
                                overflow="hidden"
                                cursor="pointer"
                                _hover={{
                                  rounded: 'lg',
                                  bg: 'blackAlpha.3',
                                }}
                              >
                                <Flex alignItems="center">
                                  <Avatar activeShake={false} size="sm" src={group?.avatar} />
                                </Flex>
                                <Box textAlign="left" minW="0">
                                  <Flex mt="0.5" align="center" gap="0.5" overflow="hidden">
                                    <GroupIcon />
                                    <Text
                                      truncate
                                      flex="1"
                                      title={group?.group_name}
                                      fontSize="sm"
                                      fontWeight="semibold"
                                      color="fg"
                                      lineHeight="normal"
                                    >
                                      {group?.group_name}
                                    </Text>
                                  </Flex>
                                  <Text
                                    color="fg.muted"
                                    fontSize="xs"
                                    fontWeight="medium"
                                    lineHeight="normal"
                                  >
                                    {group?.member_num === 1
                                      ? t('filter.membersOne')
                                      : t('filter.membersOther')}
                                  </Text>
                                </Box>
                              </Flex>
                            )}
                          </For>
                        ) : (
                          <EmptyContent>{t('chat.noCommonGroupsYet')}</EmptyContent>
                        )}
                      </Box>
                    </Stack>
                  </Popover.Body>
                </Popover.Content>
              </Popover.Positioner>
            </Portal>
          </Popover.Root>
        </HStack>
      </Box>
    </VStack>
  );
}

const memoizedMessageStrangerCard = memo(MessageStrangerCard);
export { memoizedMessageStrangerCard as MessageStrangerCard };
